import { useEffect, useState } from "react";
import { fetchMe, loginAccount, adminListInvitations, adminCreateInvitations, adminResendInvitation, adminRevokeInvitation, adminApplyInvitation, adminInvitationsBulk, AUTH_TOKEN_KEY } from "@/lib/api";
import { toast } from "sonner";
import { Compass as CompassIcon, Loader2, Copy, Check } from "lucide-react";

// BETA-ACCESS CONTROL — an administrator invites teachers by email: one form + the pending list (resend / revoke). Lives in the Teacher
// Dashboard's Admin tab (/admin); the legacy /?admin-invites page still works and renders the same panel.
const inputCls = "w-full border border-stone-300 rounded-sm px-3 py-2.5 text-[14px] focus:border-[#8C3A2A] outline-none transition-colors";
const label = "block text-[11px] uppercase tracking-[0.14em] text-stone-400 mb-1 font-mono-panel";
const btn = "inline-flex items-center justify-center gap-2 bg-[#8C3A2A] text-white text-[13.5px] px-4 py-2 rounded-sm hover:bg-[#6B2C20] transition-colors disabled:opacity-50";
const ghost = "text-[12.5px] text-[#8C3A2A] hover:underline disabled:opacity-50";

export const CopyLink = ({ url }) => {
  const [done, setDone] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(url); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* ignore */ } };
  return (
    <button type="button" onClick={copy} data-testid="invite-copy-link" className="inline-flex items-center gap-1 text-[12px] text-stone-600 hover:text-[#8C3A2A]">
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {done ? "Copied" : "Copy link"}
    </button>
  );
};

const SignIn = ({ onSignedIn }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const res = await loginAccount(form);
      localStorage.setItem(AUTH_TOKEN_KEY, res.token);
      onSignedIn(res.user);
    } catch (err) { setError(err.response?.data?.detail || "Sign in failed."); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="admin-signin-form">
      <div><label className={label}>Administrator email</label>
        <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email-input" className={inputCls} /></div>
      <div><label className={label}>Password</label>
        <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-password-input" className={inputCls} /></div>
      {error && <p data-testid="admin-signin-error" className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-sm px-3 py-2">{error}</p>}
      <button type="submit" disabled={busy} data-testid="admin-signin-button" className={btn + " w-full"}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in</button>
    </form>
  );
};

// THE ADMIN PANEL — teacher invitations (send / copy link / pending / resend / revoke). Rendered inside the Teacher Dashboard's Admin tab for
// administrators (/admin), and by the legacy /?admin-invites page below. Requires a signed-in admin `user`.
export function AdminInvitesPanel({ user, onSignOut, onChanged }) {
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState([]);
  const [pending, setPending] = useState([]);
  const reload = () => adminListInvitations("pending").then(setPending).catch(() => setPending([]));
  const changed = () => { reload(); if (onChanged) onChanged(); };
  useEffect(() => { if (user?.role === "admin") reload(); }, [user]);
  const invite = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const out = await adminCreateInvitations({ emails: [emails], role: "teacher" });
      setCreated(out); setEmails(""); changed();
    } catch (err) { setError(err.response?.data?.detail || "Could not create invitations."); }
    setBusy(false);
  };
  const resend = async (id) => { const r = await adminResendInvitation(id); setCreated([r]); changed(); };
  const revoke = async (id) => { await adminRevokeInvitation(id); changed(); };
  // APPLY TO EXISTING ACCOUNT — the invitee never used the link but created an account through the front door (usually as a student);
  // the administrator applies the invitation to that account (teacher role / course enrolment) instead of waiting on a pending that won't resolve.
  const apply = async (i) => {
    try { const r = await adminApplyInvitation(i.id); toast.success(`${r.user.email} is now a ${r.user.role}`); changed(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not apply the invitation"); }
  };
  const [sel, setSel] = useState(() => new Set());
  const toggle = (id) => setSel((s0) => { const n = new Set(s0); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOn = pending.length > 0 && pending.every((i) => sel.has(i.id));
  const bulk = async (action) => {
    const ids = [...sel];
    if (!ids.length) return;
    if (action === "revoke" && !window.confirm(`Revoke ${ids.length} invitation${ids.length === 1 ? "" : "s"}? The links stop working.`)) return;
    try { const r = await adminInvitationsBulk(ids, action); toast[r.failed.length ? "warning" : "success"](`${action === "resend" ? "Resent" : "Revoked"}: ${r.done.length}${r.failed.length ? ` · ${r.failed.length} skipped` : ""}`); setSel(new Set()); changed(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed"); }
  };
  if (!user || user.role !== "admin") {
    return (
      <div data-testid="admin-forbidden">
        <p className="text-[14px] text-stone-700 mb-3">Signed in as {user?.email} ({user?.role}). An administrator account is required.</p>
        {onSignOut ? <button onClick={onSignOut} data-testid="admin-signout" className={ghost}>Sign out</button> : null}
      </div>
    );
  }
  return (
    <div data-testid="admin-invites-panel">
      <form onSubmit={invite} className="space-y-3" data-testid="admin-invite-form">
        <div>
          <label className={label}>Teacher email addresses (comma or line separated)</label>
          <textarea rows={3} value={emails} onChange={(e) => setEmails(e.target.value)} required data-testid="admin-invite-emails" className={inputCls} placeholder="teacher@school.org, another@school.org" />
        </div>
        {error && <p data-testid="admin-invite-error" className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-sm px-3 py-2">{error}</p>}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button type="submit" disabled={busy} data-testid="admin-invite-submit" className={btn}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send teacher invitations</button>
          {onSignOut ? <span className="text-[12px] text-stone-500">Signed in as {user.email} · <button type="button" onClick={onSignOut} data-testid="admin-signout" className={ghost}>Sign out</button></span> : null}
        </div>
      </form>
      {created.length > 0 && (
        <ul className="mt-5 space-y-2" data-testid="admin-invite-results">
          {created.map((i) => (
            <li key={i.id} className="rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-stone-800 flex flex-wrap items-center justify-between gap-2">
              <span><b className="font-semibold">{i.email}</b> · {i.email_sent ? "email sent" : "email not sent — share the link"}</span>
              <CopyLink url={i.invite_url} />
            </li>
          ))}
        </ul>
      )}
      <div className="mt-7 mb-2 flex flex-wrap items-center gap-3">
        <h2 className="font-mono-panel text-[11px] uppercase tracking-[0.16em] text-stone-500">Pending invitations ({pending.length})</h2>
        {pending.length > 0 ? <label className="inline-flex items-center gap-1.5 text-[12px] text-stone-500"><input type="checkbox" data-testid="admin-invites-select-all" checked={allOn} onChange={() => setSel(allOn ? new Set() : new Set(pending.map((i) => i.id)))} /> select all</label> : null}
        {sel.size > 0 ? (
          <span data-testid="admin-invites-bulk-bar" className="inline-flex items-center gap-3 rounded-md border border-[#8C3A2A]/30 bg-[#8C3A2A]/5 px-3 py-1 text-[12.5px]">
            <b className="font-medium">{sel.size} selected</b>
            <button type="button" data-testid="admin-invites-bulk-resend" onClick={() => bulk("resend")} className={ghost}>Resend</button>
            <button type="button" data-testid="admin-invites-bulk-revoke" onClick={() => bulk("revoke")} className={ghost}>Revoke</button>
          </span>
        ) : null}
      </div>
      {pending.length === 0 ? <p className="text-[13px] text-stone-500" data-testid="admin-pending-empty">None.</p> : (
        <ul className="divide-y divide-stone-100" data-testid="admin-pending-list">
          {pending.map((i) => (
            <li key={i.id} className="py-2 text-[13px]" data-testid={`admin-pending-${i.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2"><input type="checkbox" data-testid={`admin-invite-select-${i.id}`} checked={sel.has(i.id)} onChange={() => toggle(i.id)} aria-label={`Select ${i.email}`} />
                  <span><b className="font-semibold">{i.email}</b> · {i.role} · expires {String(i.expires_at || "").slice(0, 10)}{i.email_sent ? "" : " · email not sent"}</span></span>
                <span className="flex items-center gap-3">
                  <button type="button" onClick={() => resend(i.id)} data-testid={`admin-resend-${i.id}`} className={ghost}>Resend</button>
                  <button type="button" onClick={() => revoke(i.id)} data-testid={`admin-revoke-${i.id}`} className={ghost}>Revoke</button>
                </span>
              </div>
              {i.existing_account ? (
                <div data-testid={`admin-invite-existing-${i.id}`} className="mt-1.5 ml-6 flex flex-wrap items-center gap-3 rounded-sm border border-amber-200 bg-amber-50 px-3 py-1.5 text-[12.5px] text-stone-700">
                  <span>This person already has an account{i.existing_account.name ? ` (${i.existing_account.name})` : ""} as <b>{i.existing_account.roles.join(" + ")}</b> — they never used this link.</span>
                  {i.existing_account.role !== "admin" && !i.existing_account.roles.includes(i.role)
                    ? <button type="button" data-testid={`admin-invite-apply-${i.id}`} onClick={() => apply(i)} className={ghost}>Make them a {i.role} now</button>
                    : <span className="text-stone-500">Already a {i.role} — you can revoke this invitation.</span>}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminInvites() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) { setLoading(false); return; }
    fetchMe().then(setUser).catch(() => localStorage.removeItem(AUTH_TOKEN_KEY)).finally(() => setLoading(false));
  }, []);
  const signOut = () => { localStorage.removeItem(AUTH_TOKEN_KEY); setUser(null); };
  return (
    <div className="min-h-screen bg-[#faf9f6] px-6 py-10" data-testid="admin-invites-screen">
      <div className="mx-auto w-full max-w-2xl">
        <div className="flex items-center gap-2 text-[#8C3A2A] mb-1">
          <CompassIcon className="h-5 w-5" />
          <span className="font-mono-panel text-[11px] uppercase tracking-[0.18em]">The Writing Studio · Beta access</span>
        </div>
        <h1 className="font-serif-display text-2xl text-stone-900 mb-1">Invite teachers</h1>
        <p className="text-[13.5px] text-stone-600 mb-6">Teacher accounts are created only by invitation. Each link works once and expires in 14 days. Teachers then create classes; students join with the class code. <a href="/admin" className="text-[#8C3A2A] hover:underline" data-testid="admin-invites-dashboard-link">Open the Admin tab of the dashboard</a>.</p>
        <div className="bg-white border border-stone-200 rounded-md shadow-sm p-6">
          {loading ? <p className="text-[14px] text-stone-500">Loading…</p>
            : !user ? <SignIn onSignedIn={setUser} />
            : <AdminInvitesPanel user={user} onSignOut={signOut} />}
        </div>
      </div>
    </div>
  );
}
